/**
 * SessionTimeoutProvider
 *
 * Wraps the application to provide session timeout functionality.
 * Tracks user inactivity and shows a warning modal before logging out.
 *
 * Configuration:
 * - IDLE_TIMEOUT: 30 minutes of inactivity
 * - WARNING_COUNTDOWN: 30 seconds before auto-logout
 *
 * Part of: AI Platform v5.5 - Session Management
 */

import React, { useState, useCallback } from 'react';
import { useIdleTimer } from '../hooks/useIdleTimer';
import { InactivityModal } from '../components/common/InactivityModal';
import { useAuth } from '../contexts/AuthContext';

// Configuration
const IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
const WARNING_COUNTDOWN = 30; // 30 seconds countdown

interface SessionTimeoutProviderProps {
  children: React.ReactNode;
  /** Override idle timeout in milliseconds (for testing) */
  timeout?: number;
  /** Override warning countdown in seconds (for testing) */
  countdown?: number;
}

export const SessionTimeoutProvider: React.FC<SessionTimeoutProviderProps> = ({
  children,
  timeout = IDLE_TIMEOUT,
  countdown = WARNING_COUNTDOWN
}) => {
  const [showWarning, setShowWarning] = useState(false);
  const { isAuthenticated, logout, refreshToken } = useAuth();

  // Handle when user becomes idle
  const handleIdle = useCallback(() => {
    if (isAuthenticated) {
      setShowWarning(true);
    }
  }, [isAuthenticated]);

  // Handle when user chooses to stay logged in
  const handleStayLoggedIn = useCallback(async () => {
    setShowWarning(false);
    try {
      // Refresh the token to extend the session
      await refreshToken();
    } catch (error) {
      console.error('[SessionTimeout] Failed to refresh token:', error);
    }
    // Reset the idle timer
    reset();
  }, [refreshToken]);

  // Handle logout (user choice or countdown expired)
  const handleLogout = useCallback(() => {
    setShowWarning(false);
    logout();
  }, [logout]);

  // Set up the idle timer
  const { reset, pause, resume } = useIdleTimer({
    timeout,
    onIdle: handleIdle,
    enabled: isAuthenticated,
  });

  // Pause timer while modal is shown (countdown handles this period)
  React.useEffect(() => {
    if (showWarning) {
      pause();
    } else {
      resume();
    }
  }, [showWarning, pause, resume]);

  // Handle stay logged in with timer reset
  const onStayLoggedIn = useCallback(() => {
    handleStayLoggedIn();
  }, [handleStayLoggedIn]);

  return (
    <>
      {children}
      <InactivityModal
        isOpen={showWarning && isAuthenticated}
        countdown={countdown}
        onStayLoggedIn={onStayLoggedIn}
        onLogout={handleLogout}
      />
    </>
  );
};

export default SessionTimeoutProvider;
